import type { Command } from "./commands.ts";
import { canonicalPlace, seamKey, seamsOf, sortedCrossings, withSeams } from "./layout.ts";
import { checkSeam, validateDocument } from "./layout-rules.ts";
import { sameJson } from "./same-json.ts";
import type { MapAtmosphere, MapDocument, MapGroup, MapHost, MapPlace, MapShare, Seam, SeamCrossing, SeamState, WorldDocument, WorldGrid } from "./types.ts";

/**
 * The layout commands (ADR-109 decisions 6 and 10): each changes one place, the grid, the groups, one map's
 * group or draft mark, one seam or one crossing, never a layer, and carries what it replaces so its inverse is
 * exact. A seam's head (its state and barrier) and each of its crossings are separate commands, never the whole
 * list, so two builders working on different seams, or different crossings of one seam, never overwrite each
 * other. Every command is checked against the document it leaves with `validateDocument`, so no command leaves
 * a document the reader would refuse; `placeMap` names the seams a move would orphan.
 */

/**
 * A seam's own fields beyond its pair and its crossings: its state and its barrier.
 */
export interface SeamHead {
  readonly state: SeamState;
  readonly barrier?: string;
}

/**
 * The layout commands. `placeMap`, `setGrid`, `setMapGroup`, `setMapHost` and `setMapCapacity` carry each
 * side as present or absent, absent meaning none; a place's depth of 0 is the surface, compared and written as
 * none. `setMapShare` carries each side's sharing, `open` written as none, and `setMapAtmosphere` each side's
 * whole atmosphere, empty written as none (ADR-122 decisions 1.2, 1.3, 4.2 and 6). `setSeam` with no `before` adds the seam, with `crossings` when it restores a removed one;
 * with a `before` it changes the head alone. `removeSeam` carries the whole seam it takes, so its inverse
 * restores its crossings. `setCrossing` with no `before` adds a crossing; with one it changes it, keeping its
 * name; `removeCrossing` carries the crossing it takes.
 */
export type LayoutCommand =
  | { readonly kind: "placeMap"; readonly mapId: string; readonly before?: MapPlace; readonly after?: MapPlace }
  | { readonly kind: "setGrid"; readonly before?: WorldGrid; readonly after?: WorldGrid }
  | { readonly kind: "setMapGroups"; readonly before: readonly MapGroup[]; readonly after: readonly MapGroup[] }
  | { readonly kind: "setMapGroup"; readonly mapId: string; readonly before?: string; readonly after?: string }
  | { readonly kind: "setMapDraft"; readonly mapId: string; readonly before: boolean; readonly after: boolean }
  | { readonly kind: "setMapHost"; readonly mapId: string; readonly before?: MapHost; readonly after?: MapHost }
  | { readonly kind: "setMapShare"; readonly mapId: string; readonly before: MapShare; readonly after: MapShare }
  | { readonly kind: "setMapCapacity"; readonly mapId: string; readonly before?: number; readonly after?: number }
  | { readonly kind: "setMapAtmosphere"; readonly mapId: string; readonly before: MapAtmosphere; readonly after: MapAtmosphere }
  | { readonly kind: "setSeam"; readonly a: string; readonly b: string; readonly before?: SeamHead; readonly after: SeamHead; readonly crossings?: readonly SeamCrossing[] }
  | { readonly kind: "removeSeam"; readonly seam: Seam }
  | { readonly kind: "setCrossing"; readonly a: string; readonly b: string; readonly before?: SeamCrossing; readonly after: SeamCrossing }
  | { readonly kind: "removeCrossing"; readonly a: string; readonly b: string; readonly crossing: SeamCrossing };

/**
 * Every layout command kind.
 */
export const LAYOUT_COMMAND_KINDS: readonly LayoutCommand["kind"][] = ["placeMap", "setGrid", "setMapGroups", "setMapGroup", "setMapDraft", "setMapHost", "setMapShare", "setMapCapacity", "setMapAtmosphere", "setSeam", "removeSeam", "setCrossing", "removeCrossing"];

/**
 * Whether a command is a layout command.
 *
 * @param command A command of any kind.
 * @returns True for one of `LAYOUT_COMMAND_KINDS`.
 */
export function isLayoutCommand(command: { readonly kind: string }): command is LayoutCommand {
  return (LAYOUT_COMMAND_KINDS as readonly string[]).includes(command.kind);
}

/**
 * An optional field as an object to spread: the key with its value, or nothing when the value is absent.
 *
 * @param key Field name.
 * @param value Its value, or undefined.
 * @returns `{ [key]: value }` or `{}`.
 */
function optional<K extends string, V>(key: K, value: V | undefined): { [P in K]?: V } {
  return (value === undefined ? {} : { [key]: value }) as { [P in K]?: V };
}

/**
 * A seam's head.
 *
 * @param seam The seam.
 * @returns Its state, and its barrier when it has one.
 */
export function seamHead(seam: Seam): SeamHead {
  return plainHead(seam);
}

/**
 * A head with exactly its fields: the state, and the barrier only when there is one.
 *
 * @param head The head.
 * @returns The head.
 */
function plainHead(head: SeamHead): SeamHead {
  return { state: head.state, ...optional("barrier", head.barrier) };
}

/**
 * Returns the command that exactly reverses a layout command.
 *
 * @param command The command.
 * @returns Its inverse; inverting twice gives the command back.
 */
export function invertLayoutCommand(command: LayoutCommand): LayoutCommand {
  switch (command.kind) {
    case "placeMap":
      return { kind: "placeMap", mapId: command.mapId, ...optional("before", command.after), ...optional("after", command.before) };
    case "setGrid":
      return { kind: "setGrid", ...optional("before", command.after), ...optional("after", command.before) };
    case "setMapGroup":
      return { kind: "setMapGroup", mapId: command.mapId, ...optional("before", command.after), ...optional("after", command.before) };
    case "setMapHost":
      return { kind: "setMapHost", mapId: command.mapId, ...optional("before", command.after), ...optional("after", command.before) };
    case "setMapCapacity":
      return { kind: "setMapCapacity", mapId: command.mapId, ...optional("before", command.after), ...optional("after", command.before) };
    case "setMapGroups":
    case "setMapDraft":
    case "setMapShare":
    case "setMapAtmosphere":
      return { ...command, before: command.after, after: command.before } as LayoutCommand;
    case "setSeam":
      if (command.before === undefined) {
        return { kind: "removeSeam", seam: { a: command.a, b: command.b, ...command.after, crossings: command.crossings ?? [] } };
      }
      return { kind: "setSeam", a: command.a, b: command.b, before: command.after, after: command.before };
    case "removeSeam":
      return { kind: "setSeam", a: command.seam.a, b: command.seam.b, after: seamHead(command.seam), ...(command.seam.crossings.length === 0 ? {} : { crossings: command.seam.crossings }) };
    case "setCrossing":
      if (command.before === undefined) {
        return { kind: "removeCrossing", a: command.a, b: command.b, crossing: command.after };
      }
      return { kind: "setCrossing", a: command.a, b: command.b, before: command.after, after: command.before };
    case "removeCrossing":
      return { kind: "setCrossing", a: command.a, b: command.b, after: command.crossing };
  }
}

/**
 * A world with one map replaced.
 *
 * @param world The world.
 * @param mapId The map.
 * @param change The map before, to the map after.
 * @returns The new world.
 * @throws Error when the world has no such map.
 */
function withMap(world: WorldDocument, mapId: string, change: (map: MapDocument) => MapDocument): WorldDocument {
  const index = world.maps.findIndex((m) => m.id === mapId);
  const map = world.maps[index];
  if (map === undefined) {
    throw new Error(`map ${mapId} does not exist`);
  }
  return { ...world, maps: world.maps.map((m, i) => (i === index ? change(m) : m)) };
}

/**
 * A map with one optional field set or, when the value is absent, dropped.
 *
 * @param map The map.
 * @param key The field.
 * @param value Its value, or undefined.
 * @returns The new map.
 */
function withField<K extends "place" | "host" | "share" | "capacity" | "atmosphere" | "mapGroup" | "draft">(map: MapDocument, key: K, value: MapDocument[K] | undefined): MapDocument {
  const { [key]: _previous, ...rest } = map;
  return (value === undefined ? rest : { ...rest, [key]: value }) as MapDocument;
}

/**
 * Moves, places or unplaces a map, refusing a move that would leave any of its seams without a shared edge.
 *
 * @param world The world.
 * @param command The command.
 * @returns The new world.
 * @throws Error when the map is missing, its place is not the recorded one, a seam would be orphaned (naming
 *   each), or the new place breaks a rule of `validateDocument`.
 */
function placeMap(world: WorldDocument, command: Extract<LayoutCommand, { readonly kind: "placeMap" }>): WorldDocument {
  const next = withMap(world, command.mapId, (map) => {
    if (!sameJson(canonicalPlace(map.place), canonicalPlace(command.before))) {
      throw new Error(`map ${map.id} place does not match the recorded before place`);
    }
    return withField(map, "place", canonicalPlace(command.after));
  });
  const orphaned = seamsOf(next, command.mapId).filter((seam) => {
    try {
      checkSeam(next, seam);
      return false;
    } catch {
      return true;
    }
  });
  if (orphaned.length === 1) {
    throw new Error(`map ${command.mapId} cannot move: seam ${seamKey(orphaned[0] as Seam)} would lose its shared edge`);
  }
  if (orphaned.length > 1) {
    throw new Error(`map ${command.mapId} cannot move: seams ${orphaned.map(seamKey).join(", ")} would lose their shared edge`);
  }
  return next;
}

/**
 * Finds a seam by its pair.
 *
 * @param world The world.
 * @param a Map `a`.
 * @param b Map `b`.
 * @returns The seam, or undefined.
 */
export function seamOf(world: WorldDocument, a: string, b: string): Seam | undefined {
  return (world.seams ?? []).find((seam) => seam.a === a && seam.b === b);
}

/**
 * A world with one seam replaced, added or, when `next` is null, removed.
 *
 * @param world The world.
 * @param a Map `a`.
 * @param b Map `b`.
 * @param next The seam after, or null to remove it.
 * @returns The new world, its seams in key order.
 */
function withSeam(world: WorldDocument, a: string, b: string, next: Seam | null): WorldDocument {
  const others = (world.seams ?? []).filter((seam) => !(seam.a === a && seam.b === b));
  return withSeams(world, next === null ? others : [...others, next]);
}

/**
 * Sets a seam's head, adding the seam when the command carries no `before`.
 *
 * @param world The world.
 * @param command The command.
 * @returns The new world.
 * @throws Error when an added seam exists already, a changed one does not or holds another head, or a change
 *   carries crossings.
 */
function setSeam(world: WorldDocument, command: Extract<LayoutCommand, { readonly kind: "setSeam" }>): WorldDocument {
  const held = seamOf(world, command.a, command.b);
  const key = seamKey(command);
  if (command.before === undefined) {
    if (held !== undefined) {
      throw new Error(`seam ${key} already exists`);
    }
    return withSeam(world, command.a, command.b, { a: command.a, b: command.b, ...plainHead(command.after), crossings: sortedCrossings(command.crossings ?? []) });
  }
  if (held === undefined) {
    throw new Error(`seam ${key} does not exist`);
  }
  if (command.crossings !== undefined) {
    throw new Error(`seam ${key}: a change of its state carries no crossings`);
  }
  if (!sameJson(seamHead(held), command.before)) {
    throw new Error(`seam ${key} does not match the recorded before state`);
  }
  return withSeam(world, command.a, command.b, { a: held.a, b: held.b, ...plainHead(command.after), crossings: held.crossings });
}

/**
 * Sets or removes one crossing of a seam.
 *
 * @param world The world.
 * @param a Map `a`.
 * @param b Map `b`.
 * @param before The crossing it must hold now, or undefined for none of the name.
 * @param after The crossing it takes, or undefined to remove it.
 * @returns The new world.
 * @throws Error when the seam is missing, the crossing is not the recorded one, or the two name different
 *   crossings.
 */
function setCrossing(world: WorldDocument, a: string, b: string, before: SeamCrossing | undefined, after: SeamCrossing | undefined): WorldDocument {
  const held = seamOf(world, a, b);
  const key = seamKey({ a, b });
  if (held === undefined) {
    throw new Error(`seam ${key} does not exist`);
  }
  const name = (after ?? before)?.name ?? "";
  if (before !== undefined && after !== undefined && before.name !== after.name) {
    throw new Error(`seam ${key}: a crossing keeps its name; ${before.name} is not ${after.name}`);
  }
  const current = held.crossings.find((crossing) => crossing.name === name);
  if (!sameJson(current, before)) {
    throw new Error(before === undefined ? `seam ${key}: crossing ${name} already exists` : `seam ${key}: crossing ${name} does not match the recorded crossing`);
  }
  const others = held.crossings.filter((crossing) => crossing.name !== name);
  return withSeam(world, a, b, { ...held, crossings: sortedCrossings(after === undefined ? others : [...others, after]) });
}

/**
 * Applies a layout command and checks the document it leaves.
 *
 * @param world The document; never mutated.
 * @param command The command.
 * @returns The new document.
 * @throws Error when a recorded `before` does not match the document, a target is missing, or the result breaks
 *   a rule of `validateDocument`.
 */
export function applyLayoutCommand(world: WorldDocument, command: LayoutCommand): WorldDocument {
  const next = applyUnchecked(world, command);
  validateDocument(next);
  return next;
}

/**
 * Applies a layout command without the final check.
 *
 * @param world The document.
 * @param command The command.
 * @returns The new document.
 * @throws Error when a recorded `before` does not match the document or a target is missing.
 */
function applyUnchecked(world: WorldDocument, command: LayoutCommand): WorldDocument {
  switch (command.kind) {
    case "placeMap":
      return placeMap(world, command);
    case "setGrid": {
      if (!sameJson(world.grid, command.before)) {
        throw new Error("the grid does not match the recorded before grid");
      }
      const { grid: _previous, ...rest } = world;
      return command.after === undefined ? rest : { ...rest, grid: command.after };
    }
    case "setMapGroups": {
      if (!sameJson(world.mapGroups ?? [], command.before)) {
        throw new Error("the map groups do not match the recorded before groups");
      }
      const { mapGroups: _previous, ...rest } = world;
      return command.after.length === 0 ? rest : { ...rest, mapGroups: command.after };
    }
    case "setMapGroup":
      return withMap(world, command.mapId, (map) => {
        if (map.mapGroup !== command.before) {
          throw new Error(`map ${map.id} group does not match the recorded before group`);
        }
        return withField(map, "mapGroup", command.after);
      });
    case "setMapDraft":
      return withMap(world, command.mapId, (map) => {
        if ((map.draft === true) !== command.before) {
          throw new Error(`map ${map.id} draft does not match the recorded before state`);
        }
        return withField(map, "draft", command.after ? true : undefined);
      });
    case "setMapHost":
      return withMap(world, command.mapId, (map) => {
        if (!sameJson(map.host, command.before)) {
          throw new Error(`map ${map.id} host does not match the recorded before host`);
        }
        return withField(map, "host", command.after === undefined ? undefined : { map: command.after.map, arrival: command.after.arrival });
      });
    case "setMapShare":
      return withMap(world, command.mapId, (map) => {
        if ((map.share ?? "open") !== command.before) {
          throw new Error(`map ${map.id} share does not match the recorded before share`);
        }
        return withField(map, "share", command.after === "open" ? undefined : command.after);
      });
    case "setMapCapacity":
      return withMap(world, command.mapId, (map) => {
        if (map.capacity !== command.before) {
          throw new Error(`map ${map.id} capacity does not match the recorded before capacity`);
        }
        return withField(map, "capacity", command.after);
      });
    case "setMapAtmosphere":
      return withMap(world, command.mapId, (map) => {
        if (!sameJson(map.atmosphere ?? {}, command.before)) {
          throw new Error(`map ${map.id} atmosphere does not match the recorded before atmosphere`);
        }
        return withField(map, "atmosphere", Object.keys(command.after).length === 0 ? undefined : command.after);
      });
    case "setSeam":
      return setSeam(world, command);
    case "removeSeam": {
      const held = seamOf(world, command.seam.a, command.seam.b);
      if (held === undefined || !sameJson(held, command.seam)) {
        throw new Error(`seam ${seamKey(command.seam)} does not match the recorded seam`);
      }
      return withSeam(world, held.a, held.b, null);
    }
    case "setCrossing":
      return setCrossing(world, command.a, command.b, command.before, command.after);
    case "removeCrossing":
      return setCrossing(world, command.a, command.b, command.crossing, undefined);
  }
}

/**
 * The command that removes a seam as it stands.
 *
 * @param world The world.
 * @param a Map `a`.
 * @param b Map `b`.
 * @returns The command, or null when the world has no such seam.
 */
export function removeSeamCommand(world: WorldDocument, a: string, b: string): LayoutCommand | null {
  const seam = seamOf(world, a, b);
  return seam === undefined ? null : { kind: "removeSeam", seam };
}

/**
 * The command that removes a map with every seam naming it (ADR-109 decision 6): the seams first, then the map,
 * as one compound command, so one undo restores the map and its seams exactly. A map named by no seam is one
 * `removeMap`.
 *
 * @param world The world.
 * @param mapId The map.
 * @returns The command.
 * @throws Error when the world has no such map.
 */
export function removeMapCommand(world: WorldDocument, mapId: string): Command {
  const index = world.maps.findIndex((m) => m.id === mapId);
  const map = world.maps[index];
  if (map === undefined) {
    throw new Error(`map ${mapId} does not exist`);
  }
  const removal: Command = { kind: "removeMap", index, map };
  const seams = seamsOf(world, mapId);
  return seams.length === 0 ? removal : { kind: "compound", commands: [...seams.map((seam): Command => ({ kind: "removeSeam", seam })), removal] };
}
